import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ShieldCheck, Users, Coins, Package, Trophy, 
    Gift, Plus, Trash, ChartLine, ArrowClockwise,
    MagnifyingGlass, CaretLeft, CaretRight, Robot, CurrencyBtc, Lightning, Wrench, PencilSimple, Bell
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [items, setItems] = useState([]);
    const [challenges, setChallenges] = useState([]);
    const [airdrops, setAirdrops] = useState([]);

    // Users tab
    const [usersList, setUsersList] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersPage, setUsersPage] = useState(1);
    const [usersSearch, setUsersSearch] = useState('');

    // Boltz Supply
    const [rmrSupply, setRmrSupply] = useState(null);

    // Audit log
    const [transactions, setTransactions] = useState([]);
    const [txTotal, setTxTotal] = useState(0);
    const [txPage, setTxPage] = useState(1);

    // Mint/Burn
    const [mintForm, setMintForm] = useState({ user_id: '', amount: '', reason: '' });
    const [burnForm, setBurnForm] = useState({ user_id: '', amount: '', reason: '' });

    // Dropship
    const [dropshipStatus, setDropshipStatus] = useState(null);
    // Solana
    const [solanaConfig, setSolanaConfig] = useState(null);
    const [walletInfo, setWalletInfo] = useState(null);
    const [mintLog, setMintLog] = useState(null);

    // Maintenance
    const [maintLogs, setMaintLogs] = useState([]);
    const [maintTotal, setMaintTotal] = useState(0);
    const [maintPage, setMaintPage] = useState(1);
    const [maintFilter, setMaintFilter] = useState('');
    const [newMaint, setNewMaint] = useState({ title: '', description: '', log_type: 'update', priority: 'medium', status: 'planned', scheduled_at: '' });
    const [editingMaint, setEditingMaint] = useState(null);

    // Forms
    const [newItem, setNewItem] = useState({
        name: '', description: '', category: 'gear', subcategory: '',
        brand: '', price_boltz: '', price_cad: '', stock: '', image_url: '', is_featured: false
    });
    const [newChallenge, setNewChallenge] = useState({
        title: '', description: '', category: 'distance', difficulty: 'rookie',
        requirement: '', requirement_unit: 'km', reward_boltz: '', reward_xp: ''
    });
    const [newAirdrop, setNewAirdrop] = useState({
        latitude: '', longitude: '', value: '', description: '', radius: '50'
    });

    const checkAdmin = useCallback(async () => {
        try {
            const res = await fetch(`${API}/admin/check`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setIsAdmin(data.isAdmin);
                if (data.isAdmin) fetchAllData();
            }
        } catch (e) {
            console.error("Admin fetch error:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { checkAdmin(); }, [checkAdmin]);

    const fetchAllData = async () => {
        try {
            const [statsRes, itemsRes, challengesRes, airdropsRes, supplyRes] = await Promise.all([
                fetch(`${API}/admin/stats`, { credentials: 'include' }),
                fetch(`${API}/marketplace/items`, { credentials: 'include' }),
                fetch(`${API}/challenges`, { credentials: 'include' }),
                fetch(`${API}/airdrops/nearby?latitude=0&longitude=0&radius=999999`, { credentials: 'include' }),
                fetch(`${API}/admin/boltz-supply`, { credentials: 'include' }),
            ]);
            if (statsRes.ok) setStats(await statsRes.json());
            if (itemsRes.ok) setItems(await itemsRes.json());
            if (challengesRes.ok) setChallenges(await challengesRes.json());
            if (airdropsRes.ok) setAirdrops(await airdropsRes.json());
            if (supplyRes.ok) setRmrSupply(await supplyRes.json());
        } catch (e) { console.error("Admin action error:", e); }
    };

    const fetchUsers = useCallback(async () => {
        try {
            const res = await fetch(`${API}/admin/users?page=${usersPage}&limit=20&search=${usersSearch}`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setUsersList(data.users);
                setUsersTotal(data.total);
            }
        } catch (e) { console.error("Admin action error:", e); }
    }, [usersPage, usersSearch]);

    const fetchTransactions = useCallback(async () => {
        try {
            const res = await fetch(`${API}/admin/transactions?page=${txPage}&limit=50`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setTransactions(data.transactions);
                setTxTotal(data.total);
            }
        } catch (e) { console.error("Admin action error:", e); }
    }, [txPage]);

    const fetchDropshipStatus = async () => {
        try {
            const res = await fetch(`${API}/admin/dropship-status`, { credentials: 'include' });
            if (res.ok) setDropshipStatus(await res.json());
        } catch (e) { console.error("Admin action error:", e); }
    };

    const fetchSolanaConfig = async () => {
        try {
            const res = await fetch(`${API}/admin/solana-config`, { credentials: 'include' });
            if (res.ok) setSolanaConfig(await res.json());
        } catch (e) { console.error("Admin action error:", e); }
        fetchWalletInfo();
    };

    const fetchWalletInfo = async () => {
        try {
            const res = await fetch(`${API}/admin/wallet-info`, { credentials: 'include' });
            if (res.ok) setWalletInfo(await res.json());
        } catch (e) { console.error("Admin action error:", e); }
    };

    const fetchMintLog = async () => {
        try {
            const res = await fetch(`${API}/admin/mint-log`, { credentials: 'include' });
            if (res.ok) { const d = await res.json(); setMintLog(d.log); }
        } catch (e) { console.error("Admin action error:", e); }
    };

    const setupSolana = async () => {
        try {
            const res = await fetch(`${API}/admin/setup-solana`, { method: 'POST', credentials: 'include' });
            if (res.ok) { toast.success('Solana setup started! Check logs for progress.'); setTimeout(fetchSolanaConfig, 10000); }
            else toast.error('Failed to start Solana setup');
        } catch (e) { toast.error('Failed'); }
    };

    // CRUD handlers
    const createItem = async () => {
        try {
            const res = await fetch(`${API}/admin/items`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    ...newItem,
                    price_boltz: parseFloat(newItem.price_boltz),
                    price_cad: newItem.price_cad ? parseFloat(newItem.price_cad) : null,
                    stock: parseInt(newItem.stock) || 0
                })
            });
            if (res.ok) { toast.success('Item created!'); setNewItem({ name: '', description: '', category: 'gear', subcategory: '', brand: '', price_boltz: '', price_cad: '', stock: '', image_url: '', is_featured: false }); fetchAllData(); }
            else toast.error('Failed to create item');
        } catch (e) { toast.error('Failed to create item'); }
    };

    const deleteItem = async (itemId) => {
        if (!confirm('Delete this item?')) return;
        try {
            const res = await fetch(`${API}/admin/items/${itemId}`, { method: 'DELETE', credentials: 'include' });
            if (res.ok) { toast.success('Item deleted'); fetchAllData(); }
        } catch (e) { toast.error('Failed to delete'); }
    };

    const createChallenge = async () => {
        try {
            const res = await fetch(`${API}/admin/challenges`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ ...newChallenge, requirement: parseFloat(newChallenge.requirement), reward_boltz: parseFloat(newChallenge.reward_boltz), reward_xp: parseInt(newChallenge.reward_xp) || 0 })
            });
            if (res.ok) { toast.success('Challenge created!'); setNewChallenge({ title: '', description: '', category: 'distance', difficulty: 'rookie', requirement: '', requirement_unit: 'km', reward_boltz: '', reward_xp: '' }); fetchAllData(); }
            else toast.error('Failed to create challenge');
        } catch (e) { toast.error('Failed'); }
    };

    const deleteChallenge = async (id) => {
        if (!confirm('Delete?')) return;
        try { const res = await fetch(`${API}/admin/challenges/${id}`, { method: 'DELETE', credentials: 'include' }); if (res.ok) { toast.success('Deleted'); fetchAllData(); } } catch (e) { toast.error('Failed'); }
    };

    const createAirdrop = async () => {
        try {
            const res = await fetch(`${API}/admin/airdrops`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ latitude: parseFloat(newAirdrop.latitude), longitude: parseFloat(newAirdrop.longitude), value: parseFloat(newAirdrop.value), description: newAirdrop.description, radius: parseInt(newAirdrop.radius) || 50 })
            });
            if (res.ok) { toast.success('Airdrop created!'); setNewAirdrop({ latitude: '', longitude: '', value: '', description: '', radius: '50' }); fetchAllData(); }
            else toast.error('Failed');
        } catch (e) { toast.error('Failed'); }
    };

    const deleteAirdrop = async (id) => {
        if (!confirm('Delete?')) return;
        try { const res = await fetch(`${API}/admin/airdrops/${id}`, { method: 'DELETE', credentials: 'include' }); if (res.ok) { toast.success('Deleted'); fetchAllData(); } } catch (e) { toast.error('Failed'); }
    };

    const mintBoltz = async () => {
        try {
            const res = await fetch(`${API}/admin/mint-boltz`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(mintForm)
            });
            if (res.ok) { const d = await res.json(); toast.success(`Minted Boltz. New balance: ${d.new_balance}`); setMintForm({ user_id: '', amount: '', reason: '' }); fetchAllData(); fetchUsers(); }
            else toast.error('Mint failed');
        } catch (e) { toast.error('Mint failed'); }
    };

    const burnBoltz = async () => {
        try {
            const res = await fetch(`${API}/admin/burn-boltz`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(burnForm)
            });
            if (res.ok) { const d = await res.json(); toast.success(`Burned Boltz. New balance: ${d.new_balance}`); setBurnForm({ user_id: '', amount: '', reason: '' }); fetchAllData(); fetchUsers(); }
            else toast.error('Burn failed');
        } catch (e) { toast.error('Burn failed'); }
    };

    const runDropshipAgent = async () => {
        try {
            const res = await fetch(`${API}/admin/run-dropship-agent`, { method: 'POST', credentials: 'include' });
            if (res.ok) { toast.success('Dropship agent started! Products will appear shortly.'); setTimeout(fetchDropshipStatus, 5000); }
            else toast.error('Failed to start agent');
        } catch (e) { toast.error('Failed'); }
    };

    const makeAdmin = async (userId) => {
        try {
            const res = await fetch(`${API}/admin/make-admin/${userId}`, { method: 'POST', credentials: 'include' });
            if (res.ok) { toast.success('User promoted to admin'); fetchUsers(); }
        } catch (e) { toast.error('Failed'); }
    };

    const fetchMaintLogs = useCallback(async () => {
        try {
            let url = `${API}/maintenance/logs?page=${maintPage}&limit=20`;
            if (maintFilter) url += `&status=${maintFilter}`;
            const res = await fetch(url, { credentials: 'include' });
            if (res.ok) { const d = await res.json(); setMaintLogs(d.logs); setMaintTotal(d.total); }
        } catch (e) { console.error('Maintenance fetch error:', e); }
    }, [maintPage, maintFilter]);

    const createMaintLog = async () => {
        if (!newMaint.title.trim()) { toast.error('Title is required'); return; }
        try {
            const res = await fetch(`${API}/maintenance/logs`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ ...newMaint, scheduled_at: newMaint.scheduled_at || null }),
            });
            if (res.ok) { toast.success('Maintenance log created'); setNewMaint({ title: '', description: '', log_type: 'update', priority: 'medium', status: 'planned', scheduled_at: '' }); fetchMaintLogs(); }
            else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
        } catch (e) { toast.error('Failed'); }
    };

    const updateMaintLog = async (logId, updates) => {
        try {
            const res = await fetch(`${API}/maintenance/logs/${logId}`, {
                method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify(updates),
            });
            if (res.ok) { toast.success('Updated'); setEditingMaint(null); fetchMaintLogs(); }
            else toast.error('Failed to update');
        } catch (e) { toast.error('Failed'); }
    };

    const deleteMaintLog = async (logId) => {
        if (!confirm('Delete this maintenance log?')) return;
        try {
            const res = await fetch(`${API}/maintenance/logs/${logId}`, { method: 'DELETE', credentials: 'include' });
            if (res.ok) { toast.success('Deleted'); fetchMaintLogs(); }
        } catch (e) { toast.error('Failed'); }
    };

    const pushMaintAlert = async (logId) => {
        try {
            const res = await fetch(`${API}/maintenance/logs/${logId}/alert`, {
                method: 'POST', credentials: 'include',
            });
            if (res.ok) { toast.success('Alert pushed to all riders!'); }
            else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
        } catch (e) { toast.error('Failed'); }
    };

    if (loading) return <Layout><div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div></Layout>;
    if (!isAdmin) return <Layout><div className="max-w-md mx-auto px-4 py-20 text-center"><ShieldCheck size={64} className="text-muted-foreground mx-auto mb-4" /><h1 className="text-2xl font-bold mb-2 font-display">Admin Access Required</h1><p className="text-muted-foreground mb-6">You don&apos;t have admin permissions.</p><Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button></div></Layout>;

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-primary text-glow flex items-center gap-2"><ShieldCheck size={16} />ADMIN DASHBOARD</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Platform Management</h1>
                </motion.div>

                {/* Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    {[
                        { label: 'USERS', val: stats?.totalUsers || 0, icon: Users, color: 'text-primary' },
                        { label: 'RIDES', val: stats?.totalRides || 0, icon: ChartLine, color: 'text-cyan-glow' },
                        { label: 'DISTANCE', val: `${stats?.totalDistance?.toFixed(0) || 0} km`, icon: ChartLine, color: 'text-neon-green' },
                        { label: 'BOLTZ CIRCULATING', val: rmrSupply?.totalCirculating?.toFixed(0) || '0', icon: Coins, color: 'text-neon-gold' },
                    ].map((s) => (
                        <Card key={s.label} className="glass-card" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                            <CardContent className="p-4">
                                <span className="label-text">{s.label}</span>
                                <p className={`text-2xl font-black font-display mt-1 ${s.color}`}>{s.val}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <Tabs defaultValue="users" className="w-full" onValueChange={(v) => {
                    if (v === 'users') fetchUsers();
                    if (v === 'audit') fetchTransactions();
                    if (v === 'dropship') fetchDropshipStatus();
                    if (v === 'solana') { fetchSolanaConfig(); fetchMintLog(); }
                    if (v === 'maintenance') fetchMaintLogs();
                }}>
                    <TabsList className="grid w-full grid-cols-8 mb-6 h-auto">
                        <TabsTrigger value="users" className="text-xs py-2"><Users size={14} className="mr-1" />Users</TabsTrigger>
                        <TabsTrigger value="rmr" className="text-xs py-2"><Coins size={14} className="mr-1" />Boltz</TabsTrigger>
                        <TabsTrigger value="items" className="text-xs py-2"><Package size={14} className="mr-1" />Items</TabsTrigger>
                        <TabsTrigger value="challenges" className="text-xs py-2"><Trophy size={14} className="mr-1" />Quests</TabsTrigger>
                        <TabsTrigger value="audit" className="text-xs py-2"><ChartLine size={14} className="mr-1" />Audit</TabsTrigger>
                        <TabsTrigger value="dropship" className="text-xs py-2"><Robot size={14} className="mr-1" />Agent</TabsTrigger>
                        <TabsTrigger value="solana" className="text-xs py-2"><CurrencyBtc size={14} className="mr-1" />Solana</TabsTrigger>
                        <TabsTrigger value="maintenance" className="text-xs py-2" data-testid="tab-maintenance"><Wrench size={14} className="mr-1" />Maint</TabsTrigger>
                    </TabsList>

                    {/* ===== USERS TAB ===== */}
                    <TabsContent value="users">
                        <div className="flex gap-2 mb-4">
                            <div className="relative flex-1">
                                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input placeholder="Search users..." value={usersSearch} onChange={e => { setUsersSearch(e.target.value); setUsersPage(1); }}
                                    className="pl-9 bg-card border-border" data-testid="users-search" />
                            </div>
                            <Button variant="outline" onClick={fetchUsers} className="border-border"><ArrowClockwise size={16} /></Button>
                        </div>
                        <div className="space-y-2 mb-4">
                            {usersList.map(u => (
                                <Card key={u.user_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium text-sm truncate text-foreground">{u.name || u.email}</p>
                                                {u.is_admin && <Badge variant="outline" className="text-primary border-primary/30 text-[10px]">Admin</Badge>}
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate font-mono">{u.email}</p>
                                            <p className="text-xs text-muted-foreground">ID: {u.user_id?.slice(0, 20)}...</p>
                                        </div>
                                        <div className="text-right shrink-0 ml-3">
                                            <p className="font-mono text-sm font-bold text-neon-gold">{u.boltz_balance?.toFixed(2)} Boltz</p>
                                            <p className="text-xs text-muted-foreground">Lv.{u.level || 1} | {u.total_rides || 0} rides</p>
                                            {!u.is_admin && <Button size="sm" variant="ghost" className="text-xs text-primary mt-1" onClick={() => makeAdmin(u.user_id)}>Make Admin</Button>}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">{usersTotal} users total</p>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" disabled={usersPage <= 1} onClick={() => setUsersPage(p => p - 1)} className="border-border"><CaretLeft size={14} /></Button>
                                <span className="text-sm text-muted-foreground py-2">Page {usersPage}</span>
                                <Button size="sm" variant="outline" disabled={usersList.length < 20} onClick={() => setUsersPage(p => p + 1)} className="border-border"><CaretRight size={14} /></Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* ===== Boltz SUPPLY TAB ===== */}
                    <TabsContent value="rmr">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                            {[
                                { label: 'CIRCULATING', val: rmrSupply?.totalCirculating?.toFixed(2), color: 'text-neon-gold' },
                                { label: 'TOTAL MINTED', val: rmrSupply?.totalMinted?.toFixed(2), color: 'text-neon-green' },
                                { label: 'TOTAL BURNED', val: rmrSupply?.totalBurned?.toFixed(2), color: 'text-hot-pink' },
                                { label: 'TOTAL EARNED', val: rmrSupply?.totalEarned?.toFixed(2), color: 'text-primary' },
                            ].map(s => (
                                <Card key={s.label} className="glass-card">
                                    <CardContent className="p-4"><span className="label-text">{s.label}</span><p className={`text-xl font-black font-mono mt-1 ${s.color}`}>{s.val || '0'}</p></CardContent>
                                </Card>
                            ))}
                        </div>
                        <div className="grid md:grid-cols-2 gap-6">
                            <Card className="glass-card border-neon-green/20">
                                <CardHeader><CardTitle className="text-sm font-display text-neon-green flex items-center gap-2"><Plus size={16} />MINT BOLTZ</CardTitle></CardHeader>
                                <CardContent className="space-y-3">
                                    <div><Label className="text-xs text-muted-foreground">User ID</Label><Input value={mintForm.user_id} onChange={e => setMintForm(p => ({ ...p, user_id: e.target.value }))} placeholder="user_..." className="bg-card border-border" data-testid="mint-user-id" /></div>
                                    <div><Label className="text-xs text-muted-foreground">Amount</Label><Input type="number" value={mintForm.amount} onChange={e => setMintForm(p => ({ ...p, amount: e.target.value }))} placeholder="1000" className="bg-card border-border" data-testid="mint-amount" /></div>
                                    <div><Label className="text-xs text-muted-foreground">Reason</Label><Input value={mintForm.reason} onChange={e => setMintForm(p => ({ ...p, reason: e.target.value }))} placeholder="treasury_allocation" className="bg-card border-border" /></div>
                                    <Button onClick={mintBoltz} className="w-full bg-neon-green text-black hover:bg-neon-green/80 font-display" data-testid="mint-btn">Mint Boltz</Button>
                                </CardContent>
                            </Card>
                            <Card className="glass-card border-hot-pink/20">
                                <CardHeader><CardTitle className="text-sm font-display text-hot-pink flex items-center gap-2"><Trash size={16} />BURN BOLTZ</CardTitle></CardHeader>
                                <CardContent className="space-y-3">
                                    <div><Label className="text-xs text-muted-foreground">User ID</Label><Input value={burnForm.user_id} onChange={e => setBurnForm(p => ({ ...p, user_id: e.target.value }))} placeholder="user_..." className="bg-card border-border" data-testid="burn-user-id" /></div>
                                    <div><Label className="text-xs text-muted-foreground">Amount</Label><Input type="number" value={burnForm.amount} onChange={e => setBurnForm(p => ({ ...p, amount: e.target.value }))} placeholder="500" className="bg-card border-border" data-testid="burn-amount" /></div>
                                    <div><Label className="text-xs text-muted-foreground">Reason</Label><Input value={burnForm.reason} onChange={e => setBurnForm(p => ({ ...p, reason: e.target.value }))} placeholder="correction" className="bg-card border-border" /></div>
                                    <Button onClick={burnBoltz} variant="destructive" className="w-full font-display" data-testid="burn-btn">Burn Boltz</Button>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* ===== ITEMS TAB ===== */}
                    <TabsContent value="items">
                        <Card className="glass-card mb-6">
                            <CardHeader><CardTitle className="text-sm font-display text-primary">ADD ITEM</CardTitle></CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="text-xs">Name</Label><Input value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} className="bg-card border-border" data-testid="item-name" /></div>
                                    <div><Label className="text-xs">Brand</Label><Input value={newItem.brand} onChange={e => setNewItem(p => ({ ...p, brand: e.target.value }))} className="bg-card border-border" /></div>
                                </div>
                                <div><Label className="text-xs">Description</Label><Input value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))} className="bg-card border-border" /></div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div><Label className="text-xs">Category</Label>
                                        <Select value={newItem.category} onValueChange={v => setNewItem(p => ({ ...p, category: v }))}>
                                            <SelectTrigger className="bg-card border-border"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['vehicles','parts','gear','merch'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs">Price (Boltz)</Label><Input type="number" value={newItem.price_boltz} onChange={e => setNewItem(p => ({ ...p, price_boltz: e.target.value }))} className="bg-card border-border" data-testid="item-price" /></div>
                                    <div><Label className="text-xs">Stock</Label><Input type="number" value={newItem.stock} onChange={e => setNewItem(p => ({ ...p, stock: e.target.value }))} className="bg-card border-border" /></div>
                                </div>
                                <Button onClick={createItem} className="btn-primary" data-testid="create-item-btn"><Plus size={16} className="mr-1" />Add Item</Button>
                            </CardContent>
                        </Card>
                        <p className="text-sm text-muted-foreground mb-3">{items.length} items total</p>
                        <div className="space-y-2 max-h-[500px] overflow-y-auto">
                            {items.slice(0, 50).map(item => (
                                <Card key={item.item_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-sm truncate text-foreground">{item.name}</p>
                                            <div className="flex gap-2 mt-1">
                                                <Badge variant="outline" className="text-[10px] border-border">{item.category}</Badge>
                                                {item.brand && <Badge variant="outline" className="text-[10px] border-border">{item.brand}</Badge>}
                                                {item.source === 'dropship_agent' && <Badge className="text-[10px] bg-primary/20 text-primary">AI</Badge>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="font-mono text-sm text-neon-gold">{item.price_boltz} Boltz</span>
                                            <Button size="icon" variant="ghost" onClick={() => deleteItem(item.item_id)} className="text-destructive hover:text-destructive"><Trash size={16} /></Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* ===== CHALLENGES TAB ===== */}
                    <TabsContent value="challenges">
                        <Card className="glass-card mb-6">
                            <CardHeader><CardTitle className="text-sm font-display text-primary">ADD CHALLENGE</CardTitle></CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="text-xs">Title</Label><Input value={newChallenge.title} onChange={e => setNewChallenge(p => ({ ...p, title: e.target.value }))} className="bg-card border-border" /></div>
                                    <div><Label className="text-xs">Difficulty</Label>
                                        <Select value={newChallenge.difficulty} onValueChange={v => setNewChallenge(p => ({ ...p, difficulty: v }))}>
                                            <SelectTrigger className="bg-card border-border"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['rookie','rider','veteran','legend'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                </div>
                                <div><Label className="text-xs">Description</Label><Input value={newChallenge.description} onChange={e => setNewChallenge(p => ({ ...p, description: e.target.value }))} className="bg-card border-border" /></div>
                                <div className="grid grid-cols-4 gap-3">
                                    <div><Label className="text-xs">Category</Label>
                                        <Select value={newChallenge.category} onValueChange={v => setNewChallenge(p => ({ ...p, category: v }))}>
                                            <SelectTrigger className="bg-card border-border"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['distance','speed','streak','airdrop','endurance'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs">Requirement</Label><Input type="number" value={newChallenge.requirement} onChange={e => setNewChallenge(p => ({ ...p, requirement: e.target.value }))} className="bg-card border-border" /></div>
                                    <div><Label className="text-xs">Boltz Reward</Label><Input type="number" value={newChallenge.reward_boltz} onChange={e => setNewChallenge(p => ({ ...p, reward_boltz: e.target.value }))} className="bg-card border-border" /></div>
                                    <div><Label className="text-xs">XP Reward</Label><Input type="number" value={newChallenge.reward_xp} onChange={e => setNewChallenge(p => ({ ...p, reward_xp: e.target.value }))} className="bg-card border-border" /></div>
                                </div>
                                <Button onClick={createChallenge} className="btn-primary"><Plus size={16} className="mr-1" />Add Challenge</Button>
                            </CardContent>
                        </Card>
                        <div className="space-y-2">
                            {challenges.map(ch => (
                                <Card key={ch.challenge_id} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div><p className="font-medium text-sm text-foreground">{ch.title}</p><p className="text-xs text-muted-foreground">{ch.category} | {ch.difficulty} | {ch.reward_boltz} Boltz</p></div>
                                        <Button size="icon" variant="ghost" onClick={() => deleteChallenge(ch.challenge_id)} className="text-destructive"><Trash size={16} /></Button>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* ===== AUDIT LOG TAB ===== */}
                    <TabsContent value="audit">
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-muted-foreground">{txTotal} transactions total</p>
                            <Button variant="outline" size="sm" onClick={fetchTransactions} className="border-border"><ArrowClockwise size={14} className="mr-1" />Refresh</Button>
                        </div>
                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {transactions.map((tx, i) => (
                                <Card key={tx.transaction_id || i} className="glass-card">
                                    <CardContent className="p-3 flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className={`text-[10px] ${tx.amount > 0 ? 'text-neon-green border-neon-green/30' : 'text-hot-pink border-hot-pink/30'}`}>{tx.type}</Badge>
                                                <span className="text-xs text-muted-foreground font-mono">{tx.transaction_id}</span>
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1 truncate">User: {tx.user_id?.slice(0, 25)}... | {new Date(tx.created_at).toLocaleString()}</p>
                                        </div>
                                        <span className={`font-mono font-bold ${tx.amount > 0 ? 'text-neon-green' : 'text-hot-pink'}`}>
                                            {tx.amount > 0 ? '+' : ''}{tx.amount?.toFixed(2)} Boltz
                                        </span>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                        <div className="flex items-center justify-center gap-2 mt-4">
                            <Button size="sm" variant="outline" disabled={txPage <= 1} onClick={() => setTxPage(p => p - 1)} className="border-border"><CaretLeft size={14} /></Button>
                            <span className="text-sm text-muted-foreground">Page {txPage}</span>
                            <Button size="sm" variant="outline" disabled={transactions.length < 50} onClick={() => setTxPage(p => p + 1)} className="border-border"><CaretRight size={14} /></Button>
                        </div>
                    </TabsContent>

                    {/* ===== DROPSHIP AGENT TAB ===== */}
                    <TabsContent value="dropship">
                        <Card className="glass-card mb-6">
                            <CardContent className="p-6 text-center">
                                <Robot size={48} className="text-primary mx-auto mb-4" />
                                <h3 className="text-lg font-bold font-display text-foreground mb-2">AI Dropshipping Agent</h3>
                                <p className="text-sm text-muted-foreground mb-4" style={{ fontFamily: 'var(--font-body)' }}>
                                    Automatically generates realistic product listings using GPT with Alibaba pricing + 40% markup.
                                    Covers: Electric dirtbikes, eMTB, skateboards, EUC, motors, batteries, riding gear, and brand merchandise.
                                </p>
                                {dropshipStatus && (
                                    <p className="text-sm font-mono text-primary mb-4">{dropshipStatus.total_dropship_products} AI-generated products in marketplace</p>
                                )}
                                <Button onClick={runDropshipAgent} className="btn-primary" data-testid="run-agent-btn">
                                    <Robot size={18} className="mr-2" />
                                    Run Dropship Agent
                                </Button>
                            </CardContent>
                        </Card>
                        <Card className="glass-card">
                            <CardHeader><CardTitle className="text-sm font-display text-primary">PRODUCT CATEGORIES</CardTitle></CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {[
                                        'Electric Dirtbikes', 'eMTB', 'Electric Skateboards', 'EUC',
                                        'QS / Hub / Mid-Drive Motors', 'Lithium Batteries',
                                        'Helmets', 'Jerseys & Tops', 'Riding Pants', 'Gloves',
                                        'Protection & Armor', 'Spare Parts & Upgrades',
                                        'KiloVoltz Merch', 'Boltorium Merch'
                                    ].map(cat => (
                                        <div key={cat} className="p-3 rounded-lg bg-white/5 border border-white/5 text-sm text-foreground">
                                            {cat}
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-4">
                                    Brands: Fox Racing, Leatt, Thor, Alpinestars, Sur-Ron, Talaria, QS Motor, Bafang, Samsung, LG, KiloVoltz, Boltorium
                                </p>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* ===== SOLANA TAB ===== */}
                    <TabsContent value="solana">
                        <div className="grid md:grid-cols-2 gap-6 mb-6">
                            <Card className="glass-card border-primary/20">
                                <CardContent className="p-6">
                                    <h3 className="font-display text-sm text-primary mb-4">CONNECTION STATUS</h3>
                                    {solanaConfig ? (
                                        <div className="space-y-3 text-sm">
                                            <div className="flex justify-between"><span className="text-muted-foreground">Connected</span><span className={solanaConfig.status?.connected ? 'text-neon-green' : 'text-destructive'}>{solanaConfig.status?.connected ? 'Yes' : 'No'}</span></div>
                                            <div className="flex justify-between"><span className="text-muted-foreground">Mode</span><span className={solanaConfig.status?.mode === 'live' ? 'text-neon-green font-bold' : 'text-boltz-gold'}>{solanaConfig.status?.mode || 'demo'}</span></div>
                                            <div className="flex justify-between"><span className="text-muted-foreground">Cluster</span><span>{solanaConfig.status?.cluster || 'devnet'}</span></div>
                                            <div className="flex justify-between"><span className="text-muted-foreground">Version</span><span className="font-mono text-xs">{solanaConfig.status?.version || '-'}</span></div>
                                            <div className="flex justify-between"><span className="text-muted-foreground">Private Key</span><span className={solanaConfig.has_private_key ? 'text-neon-green' : 'text-destructive'}>{solanaConfig.has_private_key ? 'Configured' : 'Missing'}</span></div>
                                            <div className="flex justify-between"><span className="text-muted-foreground">Mint Address</span><span className={solanaConfig.has_mint_address ? 'text-neon-green' : 'text-destructive'}>{solanaConfig.has_mint_address ? 'Configured' : 'Not yet minted'}</span></div>
                                            {solanaConfig.status?.mint_address && (
                                                <div className="pt-2 border-t border-white/5"><span className="text-xs text-muted-foreground">Mint: </span><span className="font-mono text-xs text-primary break-all">{solanaConfig.status.mint_address}</span></div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted-foreground">Click Refresh to load...</p>
                                    )}
                                    <Button variant="outline" size="sm" onClick={fetchSolanaConfig} className="mt-4 border-border"><ArrowClockwise size={14} className="mr-1" />Refresh</Button>
                                </CardContent>
                            </Card>
                            <Card className="glass-card border-boltz-gold/20">
                                <CardContent className="p-6 text-center">
                                    <img src="/boltz-coin-new.png" alt="Boltz" className="w-16 h-16 mx-auto mb-4 object-contain" />
                                    <h3 className="text-lg font-bold font-display text-foreground mb-2">Mint Boltz Coin</h3>
                                    <p className="text-sm text-muted-foreground mb-4">Creates the BOLTZ SPL token on Solana devnet. Auto-retries airdrop every 30s until devnet is available.</p>
                                    <Button onClick={async () => { setupSolana(); fetchMintLog(); }} className="bg-boltz-gold hover:bg-boltz-gold/80 text-black font-display" data-testid="mint-boltz-btn">
                                        <Lightning size={18} weight="fill" className="mr-2" />
                                        Mint Boltz Coin
                                    </Button>
                                    {walletInfo && (
                                        <div className="mt-4 text-left p-3 rounded-xl bg-white/5 border border-white/5">
                                            <p className="text-[10px] text-muted-foreground font-display mb-1">WALLET TO FUND</p>
                                            <p className="font-mono text-xs text-primary break-all select-all">{walletInfo.pubkey}</p>
                                            {walletInfo.mint_pubkey && (
                                                <>
                                                    <p className="text-[10px] text-neon-green font-display mt-2 mb-1">BOLTZ MINTED!</p>
                                                    <p className="font-mono text-xs text-neon-green break-all">{walletInfo.mint_pubkey}</p>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                        {/* Mint Log */}
                        {mintLog && (
                            <Card className="glass-card mb-6">
                                <CardContent className="p-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-display text-sm text-muted-foreground">MINTING LOG</h3>
                                        <Button variant="ghost" size="sm" className="text-xs" onClick={fetchMintLog}><ArrowClockwise size={12} className="mr-1" />Refresh</Button>
                                    </div>
                                    <pre className="text-[10px] text-muted-foreground font-mono bg-black/30 rounded-lg p-3 max-h-48 overflow-y-auto whitespace-pre-wrap">{mintLog}</pre>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* ===== MAINTENANCE LOG TAB ===== */}
                    <TabsContent value="maintenance">
                        <Card className="glass-card mb-6">
                            <CardHeader><CardTitle className="text-sm font-display text-primary flex items-center gap-2"><Wrench size={16} />NEW MAINTENANCE LOG</CardTitle></CardHeader>
                            <CardContent className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label className="text-xs text-muted-foreground">Title</Label><Input value={newMaint.title} onChange={e => setNewMaint(p => ({ ...p, title: e.target.value }))} placeholder="v2.1 deployment..." className="bg-card border-border" data-testid="maint-title" /></div>
                                    <div><Label className="text-xs text-muted-foreground">Scheduled At</Label><Input type="datetime-local" value={newMaint.scheduled_at} onChange={e => setNewMaint(p => ({ ...p, scheduled_at: e.target.value }))} className="bg-card border-border" data-testid="maint-scheduled" /></div>
                                </div>
                                <div><Label className="text-xs text-muted-foreground">Description</Label><Input value={newMaint.description} onChange={e => setNewMaint(p => ({ ...p, description: e.target.value }))} placeholder="Details about this maintenance..." className="bg-card border-border" data-testid="maint-description" /></div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div><Label className="text-xs text-muted-foreground">Type</Label>
                                        <Select value={newMaint.log_type} onValueChange={v => setNewMaint(p => ({ ...p, log_type: v }))}>
                                            <SelectTrigger className="bg-card border-border" data-testid="maint-type"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['deployment','downtime','incident','update','hotfix','other'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs text-muted-foreground">Priority</Label>
                                        <Select value={newMaint.priority} onValueChange={v => setNewMaint(p => ({ ...p, priority: v }))}>
                                            <SelectTrigger className="bg-card border-border" data-testid="maint-priority"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['low','medium','high','critical'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                    <div><Label className="text-xs text-muted-foreground">Status</Label>
                                        <Select value={newMaint.status} onValueChange={v => setNewMaint(p => ({ ...p, status: v }))}>
                                            <SelectTrigger className="bg-card border-border" data-testid="maint-status"><SelectValue /></SelectTrigger>
                                            <SelectContent>{['planned','in_progress','completed','cancelled'].map(s => <SelectItem key={s} value={s} className="capitalize">{s.replace('_',' ')}</SelectItem>)}</SelectContent>
                                        </Select></div>
                                </div>
                                <Button onClick={createMaintLog} className="btn-primary" data-testid="create-maint-btn"><Plus size={16} className="mr-1" />Create Log</Button>
                            </CardContent>
                        </Card>

                        {/* Filters */}
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex gap-1.5">
                                {['', 'planned', 'in_progress', 'completed', 'cancelled'].map(s => (
                                    <Button key={s} size="sm" variant={maintFilter === s ? 'default' : 'outline'}
                                        className={`text-xs capitalize rounded-full ${maintFilter === s ? 'btn-primary' : 'border-border'}`}
                                        onClick={() => { setMaintFilter(s); setMaintPage(1); }}
                                        data-testid={`maint-filter-${s || 'all'}`}>
                                        {s ? s.replace('_', ' ') : 'All'}
                                    </Button>
                                ))}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{maintTotal} entries</span>
                                <Button variant="outline" size="sm" onClick={fetchMaintLogs} className="border-border"><ArrowClockwise size={14} /></Button>
                            </div>
                        </div>

                        {/* Log Entries */}
                        <div className="space-y-2 max-h-[600px] overflow-y-auto">
                            {maintLogs.length === 0 ? (
                                <Card className="glass-card"><CardContent className="p-8 text-center">
                                    <Wrench size={40} className="text-muted-foreground mx-auto mb-3" />
                                    <p className="text-muted-foreground">No maintenance logs yet</p>
                                </CardContent></Card>
                            ) : maintLogs.map(log => {
                                const priorityColor = { critical: 'text-destructive border-destructive/30', high: 'text-hot-pink border-hot-pink/30', medium: 'text-boltz-gold border-boltz-gold/30', low: 'text-muted-foreground border-border' }[log.priority] || '';
                                const statusColor = { planned: 'text-electric-blue', in_progress: 'text-boltz-gold', completed: 'text-neon-green', cancelled: 'text-muted-foreground' }[log.status] || '';
                                const isEditing = editingMaint === log.log_id;

                                return (
                                    <Card key={log.log_id} className="glass-card" data-testid={`maint-log-${log.log_id}`}>
                                        <CardContent className="p-3">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <p className="font-medium text-sm text-foreground">{log.title}</p>
                                                        <Badge variant="outline" className={`text-[10px] ${priorityColor}`}>{log.priority}</Badge>
                                                        <Badge variant="outline" className="text-[10px] border-border">{log.log_type}</Badge>
                                                        <Badge className={`text-[10px] bg-white/5 ${statusColor}`}>{log.status?.replace('_', ' ')}</Badge>
                                                    </div>
                                                    {log.description && <p className="text-xs text-muted-foreground mt-1">{log.description}</p>}
                                                    <p className="text-[10px] text-muted-foreground mt-1">
                                                        by {log.created_by_name} · {new Date(log.created_at).toLocaleString()}
                                                        {log.scheduled_at && <> · Scheduled: {new Date(log.scheduled_at).toLocaleString()}</>}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {!isEditing && log.status !== 'completed' && (
                                                        <Select value={log.status} onValueChange={v => updateMaintLog(log.log_id, { status: v })}>
                                                            <SelectTrigger className="h-7 w-28 text-[10px] bg-card border-border"><SelectValue /></SelectTrigger>
                                                            <SelectContent>{['planned','in_progress','completed','cancelled'].map(s => <SelectItem key={s} value={s} className="text-xs capitalize">{s.replace('_',' ')}</SelectItem>)}</SelectContent>
                                                        </Select>
                                                    )}
                                                    <Button size="icon" variant="ghost" onClick={() => pushMaintAlert(log.log_id)}
                                                        className="text-boltz-gold hover:text-boltz-gold h-7 w-7" title="Push alert to riders"
                                                        data-testid={`push-alert-${log.log_id}`}>
                                                        <Bell size={14} />
                                                    </Button>
                                                    <Button size="icon" variant="ghost" onClick={() => deleteMaintLog(log.log_id)} className="text-destructive hover:text-destructive h-7 w-7"><Trash size={14} /></Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {maintTotal > 20 && (
                            <div className="flex items-center justify-center gap-2 mt-4">
                                <Button size="sm" variant="outline" disabled={maintPage <= 1} onClick={() => setMaintPage(p => p - 1)} className="border-border"><CaretLeft size={14} /></Button>
                                <span className="text-sm text-muted-foreground">Page {maintPage}</span>
                                <Button size="sm" variant="outline" disabled={maintLogs.length < 20} onClick={() => setMaintPage(p => p + 1)} className="border-border"><CaretRight size={14} /></Button>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
